import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { deepDifference } from '@/helpers';
import { useLeavePageGuard } from '@/hooks/useUnsavedChangesPrompt';
import { useApiMutation } from '@/modules/common';

import { useEventDetails } from '../api';
import { EventDetailsTab } from '../constants';
import { formatModelDetails, getModelRequest } from '../helpers';
import { EventFormSchemaType } from '../pages';
import { UpdateEventPayload } from '../types';
import { EventFormSchema } from '../validations';

export const useEventDetailsHandler = () => {
  const params = useParams<{ id: string; tab: EventDetailsTab }>();

  const {
    eventQuery: { data: details, isFetching, error, refetch: refetchEvent },
    refetchUploadUrl,
    uploadUrlData,
    updateEventMutation,
    refetchIdleStateUploadUrl,
  } = useEventDetails(params.id);

  const defaultValues: EventFormSchemaType = useMemo(() => {
    if (details === null) return {};

    return {
      ...details,
      aiModel: details?.aiModel || '',
      masks: details?.masks.map((mask) => ({
        maskId: mask.id,
        overlayId: mask.overlays?.[0]?.id ?? '',
      })),
      idleStateVideoPublicUrl: details?.idleStateVideoPublicUrl || '',
      styles: details?.styles.map((style) => ({ styleId: style.id })),
      updatedAt: details?.startDate
        ? format(new Date(details?.startDate), 'yyyy-MM-dd')
        : '',
      models: details?.models
        .map(formatModelDetails)
        .flat()
        .map((model) => ({
          ...model,
          quantity:
            typeof model.quantity === 'string'
              ? Number(model.quantity)
              : model.quantity,
        })),
      types: details?.types.map((type) => type.id),
    };
  }, [details]);

  const form = useForm<EventFormSchemaType>({
    resolver: zodResolver(EventFormSchema),
    defaultValues: defaultValues,
    values: defaultValues,
    disabled: isFetching,
    mode: 'onChange',
  });

  const hasDirtyFields = Object.keys(form.formState.dirtyFields).length === 0;
  const skipUpload =
    !form.formState.dirtyFields.idleStateVideoPublicUrl &&
    !form.formState.dirtyFields.publicUrl;

  const { isModalOpen, confirmLeave, cancelLeave } =
    useLeavePageGuard(!hasDirtyFields);

  const filesConfig = [
    {
      key: 'publicUrl',
      uploadUrl: uploadUrlData?.signedUrl,
      fileUrl: details?.publicUrl,
      fileObject: details?.videoFileObject,
    },
    {
      key: 'idleStateVideoPublicUrl',
      uploadUrl: uploadUrlData?.idleStateVideoPublicUrl,
      fileUrl: details?.idleStateVideoPublicUrl,
      fileObject: details?.idleStateVideoFileObject,
    },
  ];

  const removeIdleStateVideoMutation = useApiMutation<void>(
    `/events/${params.id}/idle-state-video`,
    {
      method: 'DELETE',
    },
    {
      onError: () => {
        toast.error('Uh oh! Something went wrong.');
      },
    }
  );

  const updateEvent = async (data) => {
    try {
      const body = deepDifference<UpdateEventPayload>(data, defaultValues);

      if (
        body.hasOwnProperty('idleStateVideoPublicUrl') &&
        !body.idleStateVideoPublicUrl
      ) {
        await removeIdleStateVideoMutation.mutateAsync();
      }
      const updatedEventData = {
        ...body,
        masks: data.masks.map((mask) => ({
          maskId: mask.maskId,
          overlays: mask.overlayId ? [{ overlayId: mask.overlayId }] : [],
        })),
        types: data.types.map((type: string) => ({ typeId: type })),
        styles: data.styles,
        models: data.models,
        printers: data.printers,
        id: params.id,
      };

      await updateEventMutation.mutateAsync(updatedEventData);

      if (isModalOpen) {
        confirmLeave();
      }
      form.reset(data);
      toast.success(`Event “${data.name}” updated successfully!`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const refetchUploadCredentials = async () => {
    try {
      await refetchUploadUrl();
      await refetchIdleStateUploadUrl();
    } catch (error) {
      toast.error('Uh oh! Something went wrong.');
    }
  };

  const handleFormSubmit = async (skipGcrValidation?: boolean) => {
    try {
      const data = form.getValues();

      await updateEvent({
        ...data,
        models: getModelRequest(data.models),
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const onDiscardChanges = (isLeave = true) => {
    if (isLeave) confirmLeave();

    form.reset(defaultValues);
    form.trigger();
  };

  useEffect(() => {
    if (!defaultValues) return;
    form.trigger();
  }, [defaultValues]);

  return {
    details,
    isFetching,
    error,
    form,
    filesConfig,
    isModalOpen,
    skipUpload,
    refetchEvent,
    handleFormSubmit,
    refetchUploadCredentials,
    onReset: () => onDiscardChanges(false),
    isUpdateLoading: updateEventMutation.isPending,
    disabledSaveButton: hasDirtyFields,
    unsavedChangesModalProps: {
      isOpen: isModalOpen,
      onOpenChange: cancelLeave,
      onSubmit: onDiscardChanges,
      isLoading: updateEventMutation.isPending,
    },
  };
};
